interface DataChannelConfig {
  onOpen?: () => void
  onClose?: () => void
  onBufferedAmountlow?: () => void
}

// WebRTC 收到消息时的回调函数
type OnMessageCallback = (code: number, data?: Uint8Array) => void

// 数据分段大小
const SEG_PKG_SIZE = 200 * 1024

/**
 * 通过 DataChannel 发送的二进制消息中的段落标志位
 */
// 消息只需要一段
const MSG_SEG_FLAG_SINGLE = 3
// 消息需要切分成多段，第一段的标志位
const MSG_SEG_FLAG_START = 0
// 消息需要切分成多段，中间段的标志位
const MSG_SEG_FLAG_CONTINUE = 1
// 消息需要切分成多段，最后一段的标志位
const MSG_SEG_FLAG_END = 2

// 段落标志位长度
const FLAG_LEN = Uint8Array.BYTES_PER_ELEMENT
// Message code 长度
const MSG_CODE_LEN = Uint16Array.BYTES_PER_ELEMENT

// 封装 WebRTC data channel，实现分段传输数据包
export class _SegDataChannel {
  readonly _channel: RTCDataChannel

  get isOpen(): boolean {
    return this._channel.readyState === "open"
  }

  get isConnecting(): boolean {
    return this._channel.readyState === "connecting"
  }

  get isClose(): boolean {
    return (
      this._channel.readyState === "closed" ||
      this._channel.readyState === "closing"
    )
  }

  // DataChannel message 消息回调
  private onMessage: OnMessageCallback | null = null
  // 当前接收数据的 message code
  private recvMsgCode: number | null = null
  // 当前接收的数据
  private recvData: SegPkgDataView | null = null
  readonly waitOpen: Promise<void>
  private resolveOpen?: () => void

  constructor(
    channel: RTCDataChannel,
    { onOpen, onClose, onBufferedAmountlow }: DataChannelConfig = {}
  ) {
    this.waitOpen = new Promise((resolve) => {
      this.resolveOpen = resolve
    })
    this._channel = channel
    this._channel.binaryType = "arraybuffer"
    this._channel.addEventListener("open", () => {
      console.log(`SegDataChannel ${this._channel.label} is open`)
      this.resolveOpen!()
      if (onOpen != null) onOpen()
    })
    this._channel.addEventListener("close", () => {
      console.log(`SegDataChannel ${this._channel.label} is closed`)
      if (onClose != null) onClose()
    })
    this._channel.addEventListener(
      "message",
      (event: MessageEvent<ArrayBuffer>) => {
        this.onReceivePkg(event)
      }
    )
    this._channel.addEventListener("bufferedamountlow", () => {
      if (onBufferedAmountlow != null) {
        onBufferedAmountlow()
      }
    })
    this._channel.addEventListener("error", (event) => {
      console.log(`SegDataChannel ${this._channel.label} error: `, event)
    })
  }

  get bufferedAmount(): number {
    return this._channel.bufferedAmount
  }

  get bufferedAmountLowThreshold(): number {
    return this._channel.bufferedAmountLowThreshold
  }

  /**
   * 通过 WebRTC Data Channel 发送数据
   * @param code Message code
   * @param data 二进制数据
   */
  send(code: number, data?: Uint8Array): void {
    if (!this.isOpen) {
      console.warn(
        `WebRTC send data error, data channel ${this._channel.label} is not open`
      )
      return
    }

    // 内容长度
    const dataLen = data?.byteLength ?? 0
    // 数据总长度, message code length + data length
    const msgDataLen = MSG_CODE_LEN + dataLen
    // 分段数据包最大长度（不包括标志位长度）
    const limit = SEG_PKG_SIZE
    // 数据需要切分成几段
    const count = Math.ceil(msgDataLen / limit)

    // 分段发送数据包
    for (let i = 0; i < count; i++) {
      // 分段标志位
      let flag: number
      // 内容数据写入时的偏移量
      let pkgOffset = 0
      // 数据包总长度 (包括分段标志位长度)
      let pkgLen: number = FLAG_LEN + limit
      // 当前追加的内容在 data 中的偏移量
      let dataOffset: number = 0
      // 追加数据的长度
      let appendDataLen: number

      if (i === 0) {
        if (count === 1) {
          // 只有一段数据
          flag = MSG_SEG_FLAG_SINGLE
          appendDataLen = dataLen
          dataOffset = 0
          pkgLen = FLAG_LEN + msgDataLen
        } else {
          // 首段数据
          flag = MSG_SEG_FLAG_START
          // 追加消息内容时要跳过 message code
          appendDataLen = limit - MSG_CODE_LEN
          dataOffset = 0
        }
      } else if (i === count - 1) {
        // 最后一段数据
        flag = MSG_SEG_FLAG_END
        dataOffset = i * limit - MSG_CODE_LEN
        // 追加所有剩余的内容
        appendDataLen = dataLen - dataOffset
        pkgLen = FLAG_LEN + appendDataLen
      } else {
        // 中间段数据标志位
        flag = MSG_SEG_FLAG_CONTINUE
        dataOffset = i * limit - MSG_CODE_LEN
        appendDataLen = limit
      }

      // 最终发送的数据包
      const segPkgData = new ArrayBuffer(pkgLen)

      // 设置分段标志位
      const flagView = new DataView(segPkgData)
      flagView.setUint8(0, flag)
      pkgOffset = FLAG_LEN

      if (i === 0) {
        // 首段数据添加 message code
        const msgCodeView = new DataView(segPkgData, pkgOffset)
        msgCodeView.setUint16(0, code, true)
        // 首段数据写入时要跳过 message code
        pkgOffset += MSG_CODE_LEN
      }

      // 添加数据内容
      const pkgMsgView = new Uint8Array(segPkgData, pkgOffset)
      const msgDataView = new Uint8Array(
        data!.buffer,
        dataOffset + data!.byteOffset,
        appendDataLen
      )
      pkgMsgView.set(msgDataView)
      try {
        // 发送分段数据包
        this._channel.send(segPkgData)
      } catch (e) {
        console.error("SegDataChannel send data error: ", e)
      }
    }
  }

  // 收到分段数据
  private onReceivePkg(event: MessageEvent<ArrayBuffer>) {
    if (!this.isOpen) {
      console.warn(
        `SegDataChannel receive data error, data channel ${this._channel.label} is closed`
      )
      return
    }

    const data = event.data

    // 读取分段标志位
    const flagView = new DataView(data, 0, FLAG_LEN)
    const flag = flagView.getUint8(0)

    if (this.recvData === null) {
      if (flag === MSG_SEG_FLAG_CONTINUE) {
        // 第一个分段数据包丢失，无法解析后续分段数据包
        console.error("SegDataChannel receive data error, first pkg is missing")
        return
      }
      // 初始化接收数据包
      this.recvData = new SegPkgDataView()
    }

    // 当前解析的偏移量
    let offset = FLAG_LEN
    if (flag === MSG_SEG_FLAG_SINGLE || flag === MSG_SEG_FLAG_START) {
      // 解析 message code
      const msgCodeView = new DataView(data, offset, MSG_CODE_LEN)
      this.recvMsgCode = msgCodeView.getUint16(0, true)
      offset += MSG_CODE_LEN
    }

    // 追加消息内容
    const contentBuffer = new Uint8Array(data, offset)
    this.recvData.append(contentBuffer)

    if (flag === MSG_SEG_FLAG_SINGLE || flag === MSG_SEG_FLAG_END) {
      if (this.recvMsgCode === null) {
        console.error("SegDataChannel receive data error, missing message code")
        return
      }
      // 当前数据接收完毕，触发 message 消息回调
      this.onMessage?.(this.recvMsgCode, this.recvData.buffer())
      // 清空当前接收数据
      this.recvMsgCode = null
      this.recvData = null
    }
  }

  // 设置 message 消息回调函数
  setOnMessageListener(onMessage: OnMessageCallback): void {
    this.onMessage = onMessage
  }

  close(): void {
    this._channel.close()
    this.recvMsgCode = null
    this.recvData = null
  }

  waitUntilOpen(): Promise<void> {
    return this.waitOpen
  }
}

// 封装 ArrayBuffer，实现动态扩容
class SegPkgDataView {
  private _len = 0
  private _capacity = 0
  private _buffer: ArrayBuffer

  constructor(initialSize: number = SEG_PKG_SIZE) {
    this._buffer = new ArrayBuffer(initialSize)
    this._capacity = initialSize
  }

  // 追加数据
  append(data: Uint8Array): void {
    const newLen = this._len + data.byteLength
    if (newLen > this._capacity) {
      // 当前 ArrayBuffer 不够用，扩容
      this.resize(newLen)
    }
    const view = new Uint8Array(this._buffer, this._len, data.byteLength)
    view.set(data)
    this._len = newLen
  }

  buffer(): Uint8Array {
    return new Uint8Array(this._buffer, 0, this._len)
  }

  /**
   * 扩容当前的 ArrayBuffer
   * @param minLen 扩容后的最小长度
   */
  private resize(minLen: number): void {
    while (this._capacity < minLen) {
      this._capacity *= 2
    }
    const newBuf = new ArrayBuffer(this._capacity)
    const newView = new Uint8Array(newBuf)
    newView.set(new Uint8Array(this._buffer))
    this._buffer = newBuf
  }
}
